import Image from 'next/image'
import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { useExperimentalAddStaticFileHost } from '../../../../experiments/new-cdn-experiment/useExperimentalAddStaticFileHost'
import { NowGeneralContextProvider } from '../../../../now'
import {
  type ProductSearchConfig,
  ProductSearchContextProvider,
  ProductSearchPagination,
  ProductSearchRefinements,
  ProductSearchRefinementsButton,
  ProductSearchRefinementSortingSelect,
  ProductSearchRefinementStateProvider,
  ProductSearchResultInfo,
} from '../../../../product-search'
import { useStoryblokStory } from '../../../context/StoryblokStoryContext'
import { type StoryblokComponentProps } from '../../../model'
import { type StoryblokProductSearchType } from '../../../model/storyblokTypes.generated'

import { StoryblokBrandAlgoliaSearch } from './StoryblokBrandAlgoliaSearch'
import { StoryblokBrandProductSearchResults } from './StoryblokBrandProductSearchResults'

const STOCK_STATUS_FILTERS =
  '(stockStatusReason:product.stockStatus.available OR stockStatusReason:product.stockStatus.nl)'

const getBrandPageProductSearchFiltersByAkeneoBrandCode = (
  akeneoCategoryCode: string | null,
): string => {
  if (!akeneoCategoryCode) {
    return STOCK_STATUS_FILTERS
  }

  return `brandCode:${akeneoCategoryCode} AND ${STOCK_STATUS_FILTERS}`
}

const getBrandPageProductSearchBrandLogoPath = (
  brandLogo: string | undefined,
): string | null => {
  if (!brandLogo) {
    return null
  }

  return `brand-logos/${brandLogo}`
}

export const StoryblokBrandProductSearch: FC<
  StoryblokComponentProps<StoryblokProductSearchType> & {
    productSearchConfig: ProductSearchConfig
  }
> = ({ productSearchConfig }) => {
  const {
    content: { akeneoCategoryCode, brandLogo, component, title },
  } = useStoryblokStory()
  const { addStaticCdnHostToUrl } = useExperimentalAddStaticFileHost()

  if (component !== 'BrandPage') {
    throw new Error(
      'StoryblokBrandProductSearch can only be used on BrandPage component',
    )
  }

  const brandLogoUrl = getBrandPageProductSearchBrandLogoPath(
    brandLogo?.[0]?.legacyImageSrc,
  )

  const brandPageFilters =
    getBrandPageProductSearchFiltersByAkeneoBrandCode(akeneoCategoryCode)

  /* eslint-disable react/jsx-max-depth -- it's a page with multiple providers */
  return (
    <ProductSearchContextProvider productSearchConfig={productSearchConfig}>
      <NowGeneralContextProvider>
        <StoryblokBrandAlgoliaSearch filters={brandPageFilters}>
          <div
            className="flex flex-col gap-3 desktop:gap-6"
            data-qa-id="brand-page-search"
          >
            <div className="flex flex-col gap-3 desktop:gap-6">
              <ProductSearchResultInfo />
              <ProductSearchRefinementsButton className="desktop:hidden" />
            </div>
            <div className="grid gap-4 pt-5 desktop:grid-cols-10 desktop:items-start desktop:gap-6 desktop:pt-0">
              <div className="hidden flex-col gap-6 desktop:col-span-3 desktop:flex">
                {brandLogoUrl ? (
                  <div className="relative flex h-[150px] w-full max-w-[280px] items-center justify-center">
                    <Image
                      alt={title ?? ''}
                      className="object-contain"
                      fill
                      loading="lazy"
                      src={addStaticCdnHostToUrl(brandLogoUrl)}
                      unoptimized
                    />
                  </div>
                ) : null}
                <div className="flex flex-col gap-4 rounded-lg bg-light-primary-medium p-4">
                  <h5 className="text-l font-medium">
                    <FormattedMessage id="general.label.filterSearch" />
                  </h5>
                  <div className="flex flex-col gap-4">
                    <ProductSearchRefinementStateProvider>
                      <ProductSearchRefinementSortingSelect />
                      <ProductSearchRefinements />
                    </ProductSearchRefinementStateProvider>
                  </div>
                </div>
              </div>
              <div className="desktop:col-span-7">
                <StoryblokBrandProductSearchResults />
                <ProductSearchPagination />
              </div>
            </div>
          </div>
        </StoryblokBrandAlgoliaSearch>
      </NowGeneralContextProvider>
    </ProductSearchContextProvider>
  )
  /* eslint-enable react/jsx-max-depth */
}
